import { Icon, IconProps, Intent } from '@clarion/ClarionIcon';
import { ReactNode } from 'react';
import { PressEvent } from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { ClarionButton, ClarionButtonProps } from './ClarionButton';

export type ClarionTagProps = {
  children?: ReactNode;
  active?: boolean;
  icon?: IconProps['icon'];
  intent?: Intent;
  large?: boolean;
  minimal?: boolean;
  onRemove?: (e: PressEvent) => void;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  removeButtonProps?: Exclude<ClarionButtonProps, 'onPress'>;
};

const tagStyles = tv({
  base: 'inline-flex items-center justify-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap max-w-full',
  variants: {
    active: {
      true: 'ring-2 ring-purple-500 ring-offset-1 dark:ring-offset-gray-900',
    },
    intent: {
      none: '',
      primary: '',
      success: '',
      warning: '',
      danger: '',
    },
    minimal: {
      true: '',
      false: '',
    },
    large: {
      true: 'text-sm px-3 py-1 min-h-8 gap-2',
    },
  },
  compoundVariants: [
    // Solid
    {
      minimal: false,
      intent: 'none',
      class: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-white',
    },
    {
      minimal: false,
      intent: 'primary',
      class: 'bg-purple-600 text-white',
    },
    {
      minimal: false,
      intent: 'success',
      class: 'bg-green-600 text-white',
    },
    {
      minimal: false,
      intent: 'warning',
      class: 'bg-orange-600 text-white',
    },
    {
      minimal: false,
      intent: 'danger',
      class: 'bg-red-600 text-white',
    },
    // Minimal
    {
      minimal: true,
      intent: 'none',
      class: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100',
    },
    {
      minimal: true,
      intent: 'primary',
      class: 'bg-purple-400/10 dark:bg-purple-400/20 text-purple-900 dark:text-purple-100',
    },
    {
      minimal: true,
      intent: 'success',
      class: 'bg-green-400/10 dark:bg-green-400/20 text-green-900 dark:text-green-100',
    },
    {
      minimal: true,
      intent: 'warning',
      class: 'bg-orange-400/10 dark:bg-orange-400/20 text-orange-900 dark:text-orange-100',
    },
    {
      minimal: true,
      intent: 'danger',
      class: 'bg-red-400/10 dark:bg-red-400/20 text-red-900 dark:text-red-100',
    },
  ],
  defaultVariants: {
    intent: 'none',
    minimal: false,
  },
});

export function ClarionTag({
  children,
  active,
  icon,
  intent = 'none',
  large,
  minimal = false,
  onRemove,
  title,
  className,
  style,
  removeButtonProps,
}: ClarionTagProps) {
  const tagClass = tagStyles({ active, intent, large, minimal, className });

  const content = (
    <>
      {icon && <Icon icon={icon} className="shrink-0" />}
      {children && <span className="truncate">{children}</span>}
      {onRemove && (
        <ClarionButton
          className="-my-0.5 -mr-2! rounded-full!"
          size="small"
          onPress={onRemove}
          tooltip="Remove"
          icon="small-cross"
          {...removeButtonProps}
        />
      )}
    </>
  );

  return (
    <span className={tagClass} style={style} title={title}>
      {content}
    </span>
  );
}
